const DOM_MUTATION_RECOVERY_KEY = 'dom_mutation_recovery'
const DOM_MUTATION_RECOVERY_WINDOW_MS = 5 * 60 * 1000

type RecoveryRecord = {
    attempts: number,
    path: string,
    timestamp: number,
}

type HastNode = {
    children?: HastNode[],
    properties?: Record<string, unknown>,
    type?: string,
}

let lastInteractionTarget: string | null = null
let diagnosticsInstalled = false

function getSessionStorage() {
    try {
        return window.sessionStorage
    } catch (e) {
        return null
    }
}

function readRecoveryRecord(): RecoveryRecord | null {
    const storage = getSessionStorage()
    if (!storage) {
        return null
    }
    try {
        const rawValue = storage.getItem(DOM_MUTATION_RECOVERY_KEY)
        if (!rawValue) {
            return null
        }
        const value = JSON.parse(rawValue)
        if (value && typeof value.timestamp === 'number' && typeof value.path === 'string') {
            return value
        }
    } catch (e) {
        // Ignore malformed or unavailable session storage.
    }
    return null
}

function writeRecoveryRecord(record: RecoveryRecord) {
    const storage = getSessionStorage()
    if (!storage) {
        return
    }
    try {
        storage.setItem(DOM_MUTATION_RECOVERY_KEY, JSON.stringify(record))
    } catch (e) {
        // Recovery remains best-effort when storage is unavailable.
    }
}

function isRecentRecovery(record: RecoveryRecord | null, path: string, now: number) {
    return Boolean(record
        && record.path === path
        && now - record.timestamp < DOM_MUTATION_RECOVERY_WINDOW_MS)
}

function applyTranslationGuard() {
    const root = document.documentElement
    if (root) {
        root.setAttribute('translate', 'no')
        root.classList.add('notranslate')
    }
    const app = document.getElementById('app')
    if (app) {
        app.setAttribute('translate', 'no')
        app.classList.add('notranslate')
    }
}

function describeTarget(target: EventTarget | null) {
    if (!(target instanceof Element)) {
        return null
    }
    const parts: string[] = []
    let current: Element | null = target
    for (let i = 0; current && i < 4; i += 1) {
        let part = current.tagName.toLowerCase()
        if (current.id) {
            part += `#${current.id}`
        } else if (current.classList.length > 0) {
            part += `.${Array.from(current.classList).slice(0, 2).join('.')}`
        }
        parts.unshift(part)
        current = current.parentElement
    }
    return parts.join(' > ')
}

export function installDomMutationDiagnostics() {
    if (diagnosticsInstalled || typeof document === 'undefined') {
        return
    }
    diagnosticsInstalled = true
    document.addEventListener('click', (event) => {
        lastInteractionTarget = describeTarget(event.target)
    }, true)
}

export function isExternalDomMutationError(error: unknown) {
    if (!error || typeof error !== 'object') {
        return false
    }
    const candidate = error as { message?: unknown, name?: unknown }
    const message = String(candidate.message || error)
    return candidate.name === 'NotFoundError'
        && (/insertBefore/.test(message) || /removeChild/.test(message))
}

export function preventBrowserTranslationInTree(node: HastNode) {
    const currentNode = node
    if (currentNode?.type === 'element') {
        const properties = currentNode.properties || {}
        const classNames = Array.isArray(properties.className)
            ? properties.className
            : String(properties.className || '').split(' ').filter(Boolean)
        if (!classNames.includes('notranslate')) {
            classNames.push('notranslate')
        }
        properties.className = classNames
        properties.translate = 'no'
        currentNode.properties = properties
    }
    if (currentNode?.children) {
        currentNode.children.forEach(preventBrowserTranslationInTree)
    }
}

export function applyDomMutationRecoveryGuard(now = Date.now()) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return false
    }
    const record = readRecoveryRecord()
    const path = window.location.pathname
    if (!isRecentRecovery(record, path, now)) {
        return false
    }
    applyTranslationGuard()
    return true
}

export function prepareDomMutationRecovery(error: unknown, now = Date.now()) {
    if (!isExternalDomMutationError(error) || typeof window === 'undefined') {
        return false
    }
    const path = window.location.pathname
    const existing = readRecoveryRecord()
    const attempts = existing && isRecentRecovery(existing, path, now) ? existing.attempts + 1 : 1
    writeRecoveryRecord({ attempts, path, timestamp: now })
    applyTranslationGuard()
    return attempts === 1
}

export function getDomMutationDiagnostics(error: unknown = null) {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return {
            domMutation: isExternalDomMutationError(error),
            runtime: 'server',
        }
    }

    const html = document.documentElement
    const app = document.getElementById('app')
    const nestedFontCount = document.querySelectorAll('font > font').length
    const translatedClass = html.classList.contains('translated-ltr')
        || html.classList.contains('translated-rtl')
    const translationWidgetSelector = [
        '.skiptranslate',
        '.goog-te-banner-frame',
        'iframe[name="googleTranslateElement"]',
    ].join(', ')
    const translateWidgetCount = document.querySelectorAll(translationWidgetSelector).length

    return {
        domMutation: isExternalDomMutationError(error),
        lastInteractionTarget,
        location: {
            host: window.location.host,
            pathname: window.location.pathname,
        },
        navigator: {
            language: navigator.language,
            languages: Array.from(navigator.languages || []),
            userAgent: navigator.userAgent,
        },
        recovery: readRecoveryRecord(),
        translation: {
            detected: translatedClass || nestedFontCount > 0 || translateWidgetCount > 0,
            htmlClassName: html.className,
            htmlLang: html.lang,
            htmlTranslate: html.getAttribute('translate'),
            nestedFontCount,
            translateWidgetCount,
            appNestedFontCount: app ? app.querySelectorAll('font > font').length : 0,
        },
    }
}
